import React, { useCallback, useState } from 'react';

import { DimensionValue, LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import PointsIcon from 'components/icons/loyalty-points-icon';
import { LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { primitive, spacing } from 'styles/constants';

import { IQuestStep } from '../loyalty-quest/types';

interface IQuestProgressBarProps {
  steps: IQuestStep[];
}

const TRACK_HEIGHT = 10;
const DOT_SIZE = 4;
const TRACK_DOT_PADDING = 8;
const CROWN_ICON_SIZE = 40;
const ARROW_HEIGHT = 5;

/**
 * Returns the pixel width the fill indicator should occupy.
 *
 * Dots are laid out with `justifyContent: 'space-between'` inside a
 * `paddingHorizontal: TRACK_DOT_PADDING` container, so the center of
 * dot at index i is:
 *   TRACK_DOT_PADDING + DOT_SIZE/2 + i × dotStep
 * where dotStep = (trackWidth − 2×TRACK_DOT_PADDING − DOT_SIZE) / (totalSteps − 1)
 *
 * The fill ends at the right edge of the last completed dot:
 *   dotCenter + DOT_SIZE/2  =  TRACK_DOT_PADDING + DOT_SIZE + lastIndex × dotStep
 */
export function computeFillWidth(
  completedSteps: number,
  totalSteps: number,
  trackWidth: number
): number {
  if (completedSteps === 0 || trackWidth === 0) {
    return 0;
  }
  if (completedSteps === totalSteps) {
    return trackWidth;
  }
  const lastIndex = completedSteps - 1;
  const dotStep = (trackWidth - 2 * TRACK_DOT_PADDING - DOT_SIZE) / (totalSteps - 1);
  const dotCenter = TRACK_DOT_PADDING + DOT_SIZE / 2 + lastIndex * dotStep;
  return dotCenter + DOT_SIZE / 2 + TRACK_DOT_PADDING;
}

export const QuestProgressBar = ({ steps }: IQuestProgressBarProps) => {
  const [trackWidth, setTrackWidth] = useState(0);
  const onTrackLayout = useCallback(
    (e: LayoutChangeEvent) => setTrackWidth(e.nativeEvent.layout.width),
    []
  );

  if (steps.length <= 1) {
    return null;
  }

  const totalSteps = steps.length;
  const completedSteps = steps.filter(s => s.status === LoyaltyQuestStepStatus.COMPLETED).length;
  const milestones = steps
    .map((s, i) =>
      s.bonusPoints
        ? {
            index: i,
            points: s.bonusPoints,
            isCompleted: s.status === LoyaltyQuestStepStatus.COMPLETED,
          }
        : null
    )
    .filter((m): m is { index: number; points: number; isCompleted: boolean } => m !== null);

  const fillWidth = computeFillWidth(completedSteps, totalSteps, trackWidth);

  return (
    <View style={styles.container}>
      {milestones.length > 0 && (
        <View style={styles.scoreDisplay}>
          {milestones.map(milestone => (
            <View
              key={milestone.index}
              style={[
                styles.crownMarker,
                {
                  left: `${(milestone.index / (totalSteps - 1)) * 100}%` as DimensionValue,
                },
              ]}
            >
              <View style={styles.crownIconWrapper}>
                <PointsIcon
                  width={`${CROWN_ICON_SIZE}px`}
                  height={`${CROWN_ICON_SIZE}px`}
                  fill={milestone.isCompleted ? primitive.$meltyYellow : primitive.$blackOpacity10}
                />
                <View style={styles.crownTextOverlay}>
                  <Typography
                    style={[
                      styles.crownText,
                      {
                        color: milestone.isCompleted
                          ? primitive.$bbqBrown
                          : primitive.$disabledText,
                      },
                    ]}
                    variant="copyTwo"
                    fontFamily="heading"
                  >
                    {milestone.points}
                  </Typography>
                </View>
              </View>
              <View
                style={[
                  styles.crownArrow,
                  {
                    borderTopColor: milestone.isCompleted
                      ? primitive.$meltyYellow
                      : primitive.$blackOpacity10,
                  },
                ]}
              />
            </View>
          ))}
        </View>
      )}
      <View style={styles.track} onLayout={onTrackLayout}>
        <View
          style={[
            styles.fill,
            {
              width: fillWidth,
            },
          ]}
        />
        <View style={styles.dotsContainer}>
          {steps.map((step, index) => (
            <View
              key={step.id || `step-${index}`}
              style={[
                styles.dot,
                step.status === LoyaltyQuestStepStatus.COMPLETED
                  ? styles.dotCompleted
                  : styles.dotPending,
              ]}
            />
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  scoreDisplay: {
    position: 'relative',
    height: CROWN_ICON_SIZE + ARROW_HEIGHT + spacing.$1,
    marginBottom: spacing.$1,
    marginLeft: TRACK_DOT_PADDING + DOT_SIZE / 2,
    marginRight: TRACK_DOT_PADDING + DOT_SIZE / 2,
  },
  crownMarker: {
    position: 'absolute',
    bottom: 0,
    alignItems: 'center',
    transform: [{ translateX: -(CROWN_ICON_SIZE / 2) }],
  },
  crownIconWrapper: {
    width: CROWN_ICON_SIZE,
    height: CROWN_ICON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  crownTextOverlay: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ translateY: 3 }],
  },
  crownText: {
    // fontSize: 8,
    // lineHeight: 10,
  },
  crownArrow: {
    width: 0,
    height: 0,
    borderLeftWidth: 4,
    borderRightWidth: 4,
    borderTopWidth: ARROW_HEIGHT,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    marginTop: 1,
  },
  track: {
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    backgroundColor: primitive.$blackOpacity10,
  },
  fill: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    borderTopLeftRadius: TRACK_HEIGHT / 2,
    borderBottomLeftRadius: TRACK_HEIGHT / 2,
    borderTopRightRadius: TRACK_HEIGHT / 2,
    borderBottomRightRadius: TRACK_HEIGHT / 2,
    backgroundColor: primitive.$meltyYellow,
  },
  dotsContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: TRACK_DOT_PADDING,
  },
  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
  },
  dotCompleted: {
    backgroundColor: primitive.$white,
  },
  dotPending: {
    backgroundColor: primitive.$blackOpacity20,
  },
});
